import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { IEffect } from '../../../../../../shared/modules/dynamic-effects/effect.interface';
import {
  LOAD_ADDRESS,
  LOAD_ADDRESS_FAIL,
  LoadPointOfDeliveryAddress,
  LoadPointOfDeliveryAddressComplete,
  LoadPointOfDeliveryAddressFail,
  LoadPointOfDeliveryNewAddressFail,
} from '../../../actions/point-of-delivery-actions/point-of-delivery.actions';
import {
  IAddressResponse,
  IRequestedAddressResponse,
} from '@cemex-core/cmx-api/models/addresses-req-management/addresses-response';
import {
  AddressesService,
  IShipmentLocationResponse,
  OpportunityService,
  SHIPMENT_LOCATION_TYPE_ID,
  ShipmentLocationsService,
} from '@cemex-core/cmx-api';
import { OpportunityResponse } from '@cemex-core/cmx-api/models/opportunity/opportunity-response';
import { ProductType } from '@cemex-core/cmx-api/models/business-lines/product-type';
import { preparePointOfDeliveryByIdRequest } from '../../../helpers/location.helper';
import { getSelectedBusinessLine, OrderBuilderState } from '../../../../../reducers';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

@Injectable()
export class PointOfDeliveryLoadAddressNewPODEffects implements IEffect {
  PointOfDeliveryLoadAddressNewPOD$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPointOfDeliveryAddress>(LOAD_ADDRESS),
      switchMap((action: LoadPointOfDeliveryAddress) => {
        if (
          action.payload.shipmentLocationType &&
          action.payload.shipmentLocationType.shipmentLocationTypeId ===
            SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY_REQUEST
        ) {
          return this.opportunityService.getOpportunity(action.payload.shipmentLocationId).pipe(
            switchMap((opportunity: OpportunityResponse) =>
              this.addressesService.getAddressRequest(opportunity.addressRequest.addressRequestId),
            ),
            map((address: IRequestedAddressResponse) => new LoadPointOfDeliveryAddressComplete(address)),
            catchError((error) => of(new LoadPointOfDeliveryAddressFail(error))),
          );
        }
        return this.addressesService.getAddress(action.payload.address.addressId).pipe(
          map((addressResponse: IAddressResponse) => {
            return new LoadPointOfDeliveryAddressComplete(addressResponse);
          }),
          catchError((error) => of(new LoadPointOfDeliveryAddressFail(action.payload))),
        );
      }),
    ),
  );

  PointOfDeliveryAddressLoadFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_ADDRESS_FAIL),
      switchMap((action: any) => {
        return this.store
          .select(getSelectedBusinessLine)
          .pipe(take(1))
          .pipe(
            map((selectedBusinessLine: ProductType) => _safe(() => selectedBusinessLine.productTypeId)),
            switchMap((businessLineId: number) => {
              return this.shipmentLocationService
                .getShipmentLocations(preparePointOfDeliveryByIdRequest(action.payload, businessLineId))
                .pipe(
                  switchMap((shipmentLocationResponse: IShipmentLocationResponse) => {
                    if (shipmentLocationResponse.shipmentLocations.length > 0) {
                      return this.addressesService
                        .getAddress(shipmentLocationResponse.shipmentLocations[0].address.addressId)
                        .pipe(
                          map((addressResponse: IAddressResponse) => {
                            return new LoadPointOfDeliveryAddressComplete(addressResponse);
                          }),
                          catchError((error) => of(new LoadPointOfDeliveryNewAddressFail(error))),
                        );
                    }
                    throw throwError(shipmentLocationResponse);
                  }),
                  catchError((error) => of(new LoadPointOfDeliveryNewAddressFail(error))),
                );
            }),
          );
      }),
    ),
  );

  constructor(
    private store: Store<OrderBuilderState>,
    private actions$: Actions,
    private addressesService: AddressesService,
    private opportunityService: OpportunityService,
    private shipmentLocationService: ShipmentLocationsService,
  ) {}
}
